import { Timestamp } from "firebase/firestore";
import type { V3Game, V3GameStatus, V3PayInfo } from "../types.v3";

const DEFAULT_PAY: V3PayInfo = {
  status: "unpaid",
  promisedAmount: 0,
};

function computeInputs(v3: Pick<V3Game, "status" | "pay" | "dateTime">): Pick<V3Game, "status" | "pay" | "dateTime"> {
  return {
    status: v3.status,
    pay: v3.pay ?? DEFAULT_PAY,
    dateTime: v3.dateTime,
  };
}

/**
 * Effective status for UI: same rules as persisted status, with safe defaults if `pay` is missing.
 */
export function effectiveGameStatusForDisplay(v3: V3Game): V3GameStatus {
  return computeGameStatus(computeInputs(v3));
}

/**
 * True when Firestore `status` disagrees with what `computeGameStatus` would persist.
 */
export function isGameStatusDrift(v3: V3Game): boolean {
  if (!v3.dateTime) return false;
  return v3.status !== computeGameStatus(computeInputs(v3));
}

export function countGamesWithStatusDrift(games: V3Game[]): number {
  let n = 0;
  for (const g of games) {
    if (isGameStatusDrift(g)) n += 1;
  }
  return n;
}

/**
 * Compute game status from pay status and date.
 * Terminal statuses (cancelled, missed) are never overwritten.
 */
export function computeGameStatus(game: Pick<V3Game, "status" | "pay" | "dateTime">): V3GameStatus {
  // Terminal states — user-set only, never auto-changed
  if (game.status === "cancelled" || game.status === "missed") {
    return game.status;
  }

  // Paid takes priority
  if (game.pay.status === "paid") {
    return "paid";
  }

  // Past game = completed
  if (game.dateTime.toMillis() < Timestamp.now().toMillis()) {
    return "completed";
  }

  return "upcoming";
}
